/**
 * 「保单录入」的**草稿模型与提交映射**（纯逻辑，**零 DOM**）。
 *
 * ## 🔴 为什么抽出来（与 `txn-draft.ts` 同因）
 *
 * 页面（`PoliciesPage`）负责"长什么样"，本文件负责"**草稿 → 请求**"：
 * ① 可测（字段多、必填与金额换算的分支多，纯函数直接用零依赖基座断言）；
 * ② 🔴 **防双实现** —— 金额边界与保险期间的判定**只调共享层内核** `checkPolicyShape()`，
 *    本文件一句规则都不重写（服务端 `InsuranceService.assertShape()` 调的是**同一个函数**）。
 *
 * ## 边界：哪些规则是本文件的、哪些不是
 *
 * | 规则 | 谁定 | 为什么 |
 * |---|---|---|
 * | 保险期间起始 ≤ 终止 · 金额整数分 / ≥ 0 · **现金价值 ≤ 保额** | **共享层内核** `checkPolicyShape` | 服务端写库前调同一个函数（`AC-B5.9`） |
 * | **保单号 / 公司 / 险种 / 当事人 / 日期 / 缴费方式非空** | **契约层**（Zod `min(1)`） | 本文件**也**判一次，但目的是**按钮可用性与就地提示**（不判就得等一次 400）—— 它**不是**这条规则的权威；服务端仍会拒，且其 `message` 会被原样展示 |
 * | 金额文本必须合法且 ≤ 两位小数 | 本文件 | 用户输入的事实；`yuanTextToCents` 对非法文本抛 `RangeError`（内核防御，不是表单错误通道）⇒ 这里"先判后转" |
 * | 🔴 **被保人 P0 只填 1 位** | **B 域 PRD §5.5.5** | 原话：「P0 即建关联表，**UI 首版只露 1 个被保人**；「多被保人」编辑入口 **P1** 打开」。⇒ 草稿是单值，**提交时包装成 1 元素数组**（契约从第一天起就是数组，P1 只改本文件的这一处与表单，不动 schema / 服务端） |
 */

import {
  PolicyStatus,
  centsToYuanText,
  checkPolicyShape,
  yuanTextToCents,
  type Cents,
  type CreatePolicyRequest,
  type PolicyView,
} from '@jiaying/shared';

/**
 * 一张保单表单的完整状态。
 *
 * ⚠️ **全部字段都是"用户输入态"**：金额是文本（`"5000"`）、日期是文本（`YYYY-MM-DD`）、
 * 状态是枚举值。换算与校验都在 `buildPolicyRequest()` 里做 ——
 * 表单自己不做任何算术（否则"元→分"的换算会出现第二份实现）。
 */
export interface PolicyDraft {
  policyNo: string;
  /** 分组键（台账按公司名分组） */
  company: string;
  insuranceType: string;
  policyholder: string;
  /** 🔴 P0 只 1 位（见文件头表格）；提交时包装成 `insureds: [insured]` */
  insured: string;
  beneficiary: string;
  startDate: string;
  /** 空串 = 未填（长期险常不填终止日） */
  endDate: string;
  /** 保额（元，文本）—— 🔴 **不计净资产** */
  sumAssuredText: string;
  /** 现金价值（元，文本）—— 🔴 **计入净资产**（限 `active` / `paid_up`） */
  cashValueText: string;
  /** 缴费方式（自由文本 —— 契约不做枚举，端侧也不硬编选项） */
  paymentMode: string;
  annualPremiumText: string;
  status: PolicyStatus;
}

/** 空草稿；`today` 作为保险期间的默认起始日（与「记一笔」同口径：账本本地日历日） */
export function emptyPolicyDraft(today: string): PolicyDraft {
  return {
    policyNo: '',
    company: '',
    insuranceType: '',
    policyholder: '',
    insured: '',
    beneficiary: '',
    startDate: today,
    endDate: '',
    sumAssuredText: '',
    cashValueText: '',
    paymentMode: '',
    annualPremiumText: '',
    // 新建默认「生效中」—— 录入当下正在持有的保单是最常见场景
    status: PolicyStatus.Active,
  };
}

/**
 * 编辑：`PolicyView` → 草稿（金额回填成元文本；被保人取第一位）。
 *
 * 🔴 **金额回填走内核 `centsToYuanText()`**，不自己写 `/ 100` ——
 * 本项目全链路**禁浮点**（规格 §22.1），ESLint 块 A 把 `/`、`Math.trunc`、`toFixed`、
 * `Intl.NumberFormat` 全项目封死、只留 `nucleus/money.ts` 白名单
 * （规则文案原文：「金额格式化请走展示层统一函数」）。
 * ⇒ 那条规则能成立的前提**就是**这个函数真的存在 —— 用它。
 *
 * ⚠️ 它恒保留 2 位小数（`"5000.00"`）—— 输入框的正则接受该形态，正好也提示了精度。
 */
export function draftFromView(view: PolicyView): PolicyDraft {
  return {
    policyNo: view.policyNo,
    company: view.company,
    insuranceType: view.insuranceType,
    policyholder: view.policyholder.name,
    insured: view.insureds[0]?.name ?? '',
    beneficiary: view.beneficiary.name,
    startDate: view.startDate,
    endDate: view.endDate ?? '',
    sumAssuredText: centsToYuanText(view.sumAssured as Cents),
    cashValueText: centsToYuanText(view.cashValue as Cents),
    paymentMode: view.paymentMode,
    annualPremiumText: centsToYuanText(view.annualPremium as Cents),
    status: view.status,
  };
}

export type PolicyBuildResult =
  | { readonly ok: true; readonly request: CreatePolicyRequest }
  | { readonly ok: false; readonly errors: readonly string[] };

const BAD_AMOUNT = '金额必填，最多两位小数（不要带千分位与币种符号）';
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 元文本 → 整数分；**非法返回 `null`**（不抛）。
 *
 * 🔴 "先判后转"：`yuanTextToCents` 对非法文本抛 `RangeError`（内核防御），
 * 表单侧自己接住它 —— 与 `txn-draft.ts` 的 `toCents` 同约定。
 */
function toCents(text: string): Cents | null {
  const trimmed = text.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return null;
  try {
    return yuanTextToCents(trimmed);
  } catch {
    return null;
  }
}

/**
 * 草稿 → `POST` / `PATCH` 的请求体。
 *
 * ⚠️ **新建与编辑共用同一个请求体**（编辑是**整字段替换**，不是打补丁 —— 契约注释原文），
 * 故两者只差调用方选哪个 HTTP 方法，本函数不需要 `mode` 参数。
 */
export function buildPolicyRequest(draft: PolicyDraft): PolicyBuildResult {
  const errors: string[] = [];

  // ── ① 必填（**UI 可用性**，权威在契约层 —— 见文件头表格第 2 行）──
  const REQUIRED: [string, string][] = [
    ['policyNo', '保单号'],
    ['company', '保险公司'],
    ['insuranceType', '险种'],
    ['policyholder', '投保人'],
    ['insured', '被保人'],
    ['beneficiary', '受益人'],
    ['paymentMode', '缴费方式'],
  ];
  for (const [key, label] of REQUIRED) {
    if (draft[key as keyof PolicyDraft].toString().trim() === '') errors.push(`${label}必填`);
  }
  if (!DAY_RE.test(draft.startDate.trim())) errors.push('保险期间起始日必填（YYYY-MM-DD）');
  if (draft.endDate.trim() !== '' && !DAY_RE.test(draft.endDate.trim())) {
    errors.push('保险期间终止日格式须为 YYYY-MM-DD');
  }

  // ── ② 金额（文本 → 整数分）──
  const sumAssured = toCents(draft.sumAssuredText);
  const cashValue = toCents(draft.cashValueText);
  const annualPremium = toCents(draft.annualPremiumText);
  if (sumAssured === null) errors.push(`保额：${BAD_AMOUNT}`);
  if (cashValue === null) errors.push(`现金价值：${BAD_AMOUNT}`);
  if (annualPremium === null) errors.push(`年缴保费：${BAD_AMOUNT}`);

  // ⚠️ 这一行同时做两件事：**早退** 与 **把三个值收窄成非 null**（TS 不会因为
  //    `errors.length > 0` 而推断出"金额不为空"—— 那是两件不同的事）。
  if (sumAssured === null || cashValue === null || annualPremium === null) {
    return { ok: false, errors };
  }

  // 有本地错误（必填 / 日期格式）⇒ 也先返回（内核拿不到合法输入，跑它只会产出连带噪音）
  if (errors.length > 0) return { ok: false, errors };

  // ── ③ 🔴 语义判定：**只调内核**（保险期间顺序 / 现金价值 ≤ 保额 / 整数分）──
  const shapeInput = {
    policyNo: draft.policyNo,
    company: draft.company,
    insuranceType: draft.insuranceType,
    startDate: draft.startDate.trim(),
    endDate: draft.endDate.trim() === '' ? null : draft.endDate.trim(),
    sumAssured,
    cashValue,
    annualPremium,
    status: draft.status,
  };
  const issues = checkPolicyShape(shapeInput);
  if (issues.length > 0) return { ok: false, errors: issues };

  return {
    ok: true,
    request: {
      policyNo: draft.policyNo.trim(),
      company: draft.company.trim(),
      insuranceType: draft.insuranceType.trim(),
      policyholder: draft.policyholder.trim(),
      // 🔴 P0：单值 → 1 元素数组（见文件头表格第 4 行）
      insureds: [draft.insured.trim()],
      beneficiary: draft.beneficiary.trim(),
      startDate: shapeInput.startDate,
      endDate: shapeInput.endDate,
      sumAssured,
      cashValue,
      paymentMode: draft.paymentMode.trim(),
      annualPremium,
      status: draft.status,
    },
  };
}
